import {
  AutoSaveList,
  EMPTY_MEDICATION_VALUES,
  MedicationFields,
  NoMedicationCheckbox,
  NoMedicationConfirmDialog,
  getMedicationRowLabel,
  mapMedicationRowToValues,
  toMedicationFieldPayload,
  useFormReadOnly,
  useNoMedicationConfirm,
} from '../../../../common';
import { TREATMENT_PLAN_V3_MEDICATION_FIELD_COLUMNS } from './constants';
import { NO_MEDICATION_LABEL_SX } from './styles';
import { MEDICATION_FORM_SCHEMA } from './validationSchema';
import { useFetchTreatmentPlanV3MedicationPage, useTreatmentPlanV3Medications } from './hooks';
import { TreatmentPlanV3PageContent, TreatmentPlanV3PageLoadState } from '../components';
import { MONTH_YEAR_DISPLAY_FORMAT } from '../../../../../constants';

import type { IMedication, MedicationFormValues, MedicationPayload } from '../../../../common';

export default function MedicationForm() {
  const { retry } = useFetchTreatmentPlanV3MedicationPage();
  const {
    medications,
    noMedication,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoMedication,
  } = useTreatmentPlanV3Medications();
  const readOnly = useFormReadOnly();
  const hasSavedMedications = medications.length > 0;
  const { isConfirmOpen, handleToggle, handleConfirm, closeConfirm } = useNoMedicationConfirm({
    hasSavedMedications,
    clearAll,
    setNoMedication,
  });

  return (
    <TreatmentPlanV3PageContent title="Medications taken by your child">
      <TreatmentPlanV3PageLoadState
        errorMessage="Unable to load medications. Please try again."
        hasLoaded={hasLoaded}
        hasLoadError={hasLoadError}
        onRetry={retry}
      >
        <NoMedicationCheckbox
          checked={noMedication}
          disabled={isLoading || readOnly || isBulkSaving}
          onChange={handleToggle}
          labelSx={NO_MEDICATION_LABEL_SX}
        />

        {noMedication ? null : (
          <AutoSaveList<MedicationFormValues, IMedication, MedicationPayload>
            addButtonLabel="Add medication"
            disabled={isLoading || isBulkSaving}
            emptyValues={EMPTY_MEDICATION_VALUES}
            entries={medications}
            getDeleteConfirm={(row) => ({
              title: 'Confirm medication deletion?',
              message: `Are you sure you want to delete medication "${row.medication_name ?? ''}"?`,
            })}
            getRowKey={(row) => row.id}
            getRowLabel={getMedicationRowLabel}
            isSaving={isSaving}
            listErrorMessage={
              medications.length === 0
                ? 'Add at least one medication or check "No medication taken by the client"'
                : undefined
            }
            mapRowToValues={mapMedicationRowToValues}
            newEntryLabel="New medication"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            plainDraft
            renderFields={({
              formik,
              disabled,
              readOnly: fieldsReadOnly,
              saveField,
              backgroundColor,
            }) => (
              <MedicationFields
                backgroundColor={backgroundColor}
                columns={TREATMENT_PLAN_V3_MEDICATION_FIELD_COLUMNS}
                disabled={disabled}
                formik={formik}
                monthFormat={MONTH_YEAR_DISPLAY_FORMAT}
                readOnly={fieldsReadOnly}
                saveField={saveField}
                startDateLabel="Start date"
                startDatePlaceholder="Select date"
              />
            )}
            toFieldPayload={toMedicationFieldPayload}
            validationSchema={MEDICATION_FORM_SCHEMA}
          />
        )}

        <NoMedicationConfirmDialog
          open={isConfirmOpen}
          hasSavedMedications={hasSavedMedications}
          confirmLoading={isBulkSaving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      </TreatmentPlanV3PageLoadState>
    </TreatmentPlanV3PageContent>
  );
}
